"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef } from "react";

const ITEMS = [
  { icon: "/images/lineboil/08-tre-personer-mini.webp", text: "462 firmaer på platformen" },
  { icon: "/images/lineboil/16-danmarkskort-mini.webp", text: "Dækker hele Danmark" },
  { icon: "/images/lineboil/03-sparegris-mini.webp", text: "Gratis og uforpligtende" },
  { icon: "/images/lineboil/06-kalender-mini.webp", text: "Matchet på 2 minutter" },
];

const SPEED = 0.5; // px per frame (~30px/s)
const GAP = 40; // matches gap-10

export function TrustMarquee() {
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const rafRef = useRef(0);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startOffsetRef = useRef(0);
  const halfWidth = useRef(0);

  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    halfWidth.current = track.scrollWidth / 2;
  }, []);

  const tick = useCallback(() => {
    if (!draggingRef.current) {
      offsetRef.current -= SPEED;
    }
    if (halfWidth.current > 0) {
      if (offsetRef.current <= -halfWidth.current) {
        offsetRef.current += halfWidth.current;
      } else if (offsetRef.current >= 0) {
        offsetRef.current -= halfWidth.current;
      }
    }
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(${offsetRef.current}px)`;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    measure();
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [measure, tick]);

  const onPointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true;
    startXRef.current = e.clientX;
    startOffsetRef.current = offsetRef.current;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    const delta = e.clientX - startXRef.current;
    offsetRef.current = startOffsetRef.current + delta;
  };

  const onPointerUp = () => {
    draggingRef.current = false;
  };

  return (
    <div
      className="overflow-hidden sm:hidden touch-pan-y select-none cursor-grab active:cursor-grabbing"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div ref={trackRef} className="flex w-max will-change-transform" style={{ gap: GAP }}>
        {[0, 1].map((copy) =>
          ITEMS.map((item) => (
            <div key={`${copy}-${item.text}`} className="flex shrink-0 items-center gap-3">
              <Image
                src={item.icon}
                alt=""
                width={40}
                height={40}
                className="h-8 w-8 shrink-0 object-contain pointer-events-none"
                draggable={false}
              />
              <span className="text-[15px] leading-tight font-medium text-ink-muted whitespace-nowrap pointer-events-none">
                {item.text}
              </span>
            </div>
          )),
        )}
      </div>
    </div>
  );
}
